import { App, Button, Checkbox, Empty, Input, QRCode, Space, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TotpStepEnum } from '../../../enums/TotpStepEnum';
import type { TotpSetupResponse, TotpStatusResponse } from '../../../interfaces/UserApiService';
import { authService } from '../../../services/authService';
import { UserService } from '../../../services/userApiService';
import { useAppStore } from '../../../store/store';
import { isTotpForcedSetupTokenInvalid } from '../../../utils/totpApiErrors';
import { Icon } from '../../Core/Icon';

// Instance of the user service used for TOTP-related API calls
const userService = new UserService();

/** Which backend endpoints/flow this form drives. See table in the forced-setup plan for the full mapping. */
export type TotpFormSetupChannel = 'session' | 'forcedLogin';

export interface TotpFormProps {
  /** Callback when the modal/content should be closed (e.g. after "Continue" on backup codes). */
  onClose?: () => void;
  /** Optional callback when an action succeeds (e.g. enable/disable) so parent can refresh (e.g. getUserInfo). */
  onSuccess?: () => void;
  /** Called when a TOTP API call returns 401 (or, in `forcedLogin`, an `INVALID_TOKEN` 400) — the temporary/partial token expired. Parent should reset the login flow. */
  onTemporaryTotpExpired?: () => void;
  /** When true, only setup and regenerate backup codes are allowed; disable 2FA is hidden (e.g. modal opened from login). */
  isSetupOnly?: boolean;
  /**
   * `'session'` (default): the existing session-token setup/verify flow (`/user/totp/*`), gated by a status check.
   * `'forcedLogin'`: mandatory setup after a `403 TOTP_SETUP_REQUIRED` login response — skips the status step and
   * uses the partial-auth-token endpoints (`/user/auth/totp/setup*`); on verify success the user is auto-logged in.
   */
  setupChannel?: TotpFormSetupChannel;
  /** Required when `setupChannel` is `'forcedLogin'`: the partial auth token from the 403 response. */
  partialAuthToken?: string;
}

/**
 * Formats a TOTP secret for better human readability.
 * E.g., groups into chunks of 4 characters with spaces between.
 */
function formatSecret(secret: string): string {
  return secret.match(/.{1,4}/g)?.join(' ') ?? secret;
}

/**
 * Determines if the error received is an HTTP 401 error (unauthorized/expired token).
 */
function is401(err: unknown): boolean {
  const e = err as { status?: number; response?: { status?: number } };
  return e?.status === 401 || e?.response?.status === 401;
}

/**
 * True when the error means the temporary/partial token can no longer be used to continue:
 * the session flow's 401, or the forced-login flow's `400 INVALID_TOKEN` (expired partial token).
 */
function isTemporaryTokenExpiredError(err: unknown, setupChannel: TotpFormSetupChannel): boolean {
  return is401(err) || (setupChannel === 'forcedLogin' && isTotpForcedSetupTokenInvalid(err));
}

/**
 * TOTP form component manages enabling/disabling TOTP,
 * verifying TOTP codes, and displaying/handling backup codes.
 */
export default function TotpForm({
  onClose,
  onSuccess,
  onTemporaryTotpExpired,
  isSetupOnly = false,
  setupChannel = 'session',
  partialAuthToken,
}: TotpFormProps) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const closeActivateTotpOverlay = useAppStore((s) => s.appEvents.activateTotp.close);

  // Current UI step in the flow
  const [step, setStep] = useState<TotpStepEnum>(TotpStepEnum.Loading);
  // TOTP enablement and policy status
  const [status, setStatus] = useState<TotpStatusResponse | null>(null);
  // Data for TOTP setup (uri, secret, etc.)
  const [setupData, setSetupData] = useState<TotpSetupResponse | null>(null);
  // Backup codes to be verified/saved on activation/regeneration
  const [verifyBackupCodes, setVerifyBackupCodes] = useState<string[]>([]);
  // Code entered by user (TOTP)
  const [code, setCode] = useState('');
  // Whether user confirmed that backup codes are saved
  const [backupCodesSaved, setBackupCodesSaved] = useState(false);
  // Whether manual entry of the secret is shown instead of QR
  const [showManualSecret, setShowManualSecret] = useState(false);
  // Any error message to display
  const [error, setError] = useState<string | null>(null);
  // UI loading state for async actions
  const [loading, setLoading] = useState(false);
  // True if we're in "regenerate backup codes" post-enablement flow
  const [isRegenerateFlow, setIsRegenerateFlow] = useState(false);

  /**
   * Fetch the current TOTP status, e.g. if enabled,
   * how many backup codes are left, when enabled, etc.
   */
  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await userService.totpStatus();
      setStatus(data);
      setStep(TotpStepEnum.Status);
    } catch (err) {
      if (is401(err)) {
        // Call parent callback if temporary token expired
        onTemporaryTotpExpired?.();
        return;
      }
      console.error('TOTP status fetch error:', err);
      setError(t('modal.totpForm.errorGeneric'));
    } finally {
      setLoading(false);
    }
  }, [t, onTemporaryTotpExpired]);

  /**
   * Handles the start of the TOTP setup flow:
   * generates a new TOTP secret and provides details for user to scan or enter.
   * In `forcedLogin` channel this uses the partial-auth-token endpoint (no Bearer session).
   */
  const handleStartSetup = useCallback(async () => {
    setLoading(true);
    setError(null);
    setCode('');
    try {
      const data =
        setupChannel === 'forcedLogin' && partialAuthToken
          ? await userService.totpForcedSetup({ partialAuthToken })
          : await userService.totpSetup();
      setSetupData(data);
      setStep(TotpStepEnum.Setup);
    } catch (err) {
      if (isTemporaryTokenExpiredError(err, setupChannel)) {
        onTemporaryTotpExpired?.();
        return;
      }
      console.error('TOTP setup error:', err);
      setError(t('modal.totpForm.errorGeneric'));
    } finally {
      setLoading(false);
    }
  }, [t, onTemporaryTotpExpired, setupChannel, partialAuthToken]);

  /** Initial step: skip the status check in `forcedLogin` (no session yet) and go straight to setup. */
  const initializeFlow = useCallback(
    () => (setupChannel === 'forcedLogin' ? handleStartSetup() : fetchStatus()),
    [setupChannel, handleStartSetup, fetchStatus],
  );

  // Fetch TOTP status (or start forced setup) on first render
  useEffect(() => {
    initializeFlow();
  }, [initializeFlow]);

  /**
   * Handles verification of the user's newly setup TOTP (after scanning QR or manual entry).
   * In `forcedLogin` channel this completes auto-login (new session tokens) instead of the
   * session-token verify endpoint.
   */
  const handleVerifySetup = useCallback(async () => {
    const trimmed = code.replace(/\s/g, ''); // Strip whitespace
    if (trimmed.length !== 6) {
      setError(t('modal.totpForm.invalidCode'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const backupCodes =
        setupChannel === 'forcedLogin' && partialAuthToken
          ? (await authService.completeForcedTotpSetupVerify(partialAuthToken, trimmed)).backupCodes
          : (await userService.totpVerify({ code: trimmed })).backupCodes;
      setVerifyBackupCodes(backupCodes);
      setIsRegenerateFlow(false);
      setStep(TotpStepEnum.BackupCodes);
    } catch (err) {
      if (isTemporaryTokenExpiredError(err, setupChannel)) {
        onTemporaryTotpExpired?.();
        return;
      }
      console.error('TOTP verify error:', err);
      setError(t('modal.totpForm.invalidCode'));
    } finally {
      setLoading(false);
    }
  }, [code, t, onTemporaryTotpExpired, setupChannel, partialAuthToken]);

  /**
   * Copies the secret to clipboard (manual TOTP setup).
   */
  const handleCopySecret = useCallback(() => {
    if (!setupData?.secret) return;
    navigator.clipboard.writeText(setupData.secret);
    message.success(t('modal.totpForm.copied'));
  }, [setupData?.secret, message, t]);

  /**
   * Copies all backup codes to clipboard for the user.
   */
  const handleCopyAllBackupCodes = useCallback(() => {
    if (verifyBackupCodes.length === 0) return;
    navigator.clipboard.writeText(verifyBackupCodes.join('\n'));
    message.success(t('modal.totpForm.copied'));
  }, [verifyBackupCodes, message, t]);

  /**
   * Downloads the backup codes as a plain text file for the user.
   */
  const handleDownloadBackupCodes = useCallback(() => {
    if (verifyBackupCodes.length === 0) return;
    const content = `FILEGRANT - TOTP Backup Codes
=====================================
Date: ${new Date().toLocaleDateString()}

${t('modal.totpForm.backupCodesWarning')}

${verifyBackupCodes.map((c, i) => `${i + 1}. ${c}`).join('\n')}

=====================================
`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'filegrant-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
    message.success(t('modal.totpForm.download'));
  }, [verifyBackupCodes, t, message]);

  /**
   * Called when user has confirmed saving of backup codes (enables/continues flow, closes modal, etc.).
   * In case of a backup code regeneration, also refetches TOTP status to update UI.
   */
  const handleContinueAfterBackupCodes = useCallback(() => {
    setVerifyBackupCodes([]);
    setBackupCodesSaved(false);
    // Force-close the parent 2FA promo overlay when setup flow is completed.
    closeActivateTotpOverlay();
    void useAppStore.getState().fetchActiveAlerts(false);
    onSuccess?.();
    onClose?.();
    // Modal parents destroy on onClose; page embed stays mounted — return to status.
    if (isRegenerateFlow || !onClose) {
      fetchStatus();
      setStep(TotpStepEnum.Status);
    }
  }, [onSuccess, onClose, isRegenerateFlow, fetchStatus, closeActivateTotpOverlay]);

  /**
   * Begins the backup code regeneration process (moves to verify code step).
   */
  const handleRegenerateBackupCodes = useCallback(() => {
    setCode('');
    setError(null);
    setStep(TotpStepEnum.RegenerateVerify);
  }, []);

  /**
   * Submits the code for regenerating backup codes, and on success
   * displays new backup codes for the user to save/print/copy.
   */
  const handleSubmitRegenerateCode = useCallback(async () => {
    const trimmed = code.replace(/\s/g, '');
    if (trimmed.length !== 6) {
      setError(t('modal.totpForm.invalidCode'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await userService.totpRegenerateBackupCodes({
        code: trimmed,
      });
      setVerifyBackupCodes(response.backupCodes ?? []);
      setIsRegenerateFlow(true);
      setStep(TotpStepEnum.BackupCodes);
    } catch (err) {
      if (is401(err)) {
        onTemporaryTotpExpired?.();
        return;
      }
      setError(t('modal.totpForm.invalidCode'));
    } finally {
      setLoading(false);
    }
  }, [code, t, onTemporaryTotpExpired]);

  /**
   * Starts the disable TOTP flow (moves to step requiring TOTP code for disabling).
   */
  const handleDisableTotp = useCallback(() => {
    setCode('');
    setError(null);
    setStep(TotpStepEnum.DisableVerify);
  }, []);

  /**
   * Submits the code to disable TOTP; on success disables TOTP and updates status.
   */
  const handleSubmitDisable = useCallback(async () => {
    const trimmed = code.replace(/\s/g, '');
    if (trimmed.length !== 6) {
      setError(t('modal.totpForm.invalidCode'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await userService.totpDisable({ code: trimmed });
      message.success(t('modal.totpForm.disabledSuccess'));
      setCode('');
      await fetchStatus();
      void useAppStore.getState().fetchActiveAlerts(false);
      setStep(TotpStepEnum.Status);
    } catch (err) {
      if (is401(err)) {
        onTemporaryTotpExpired?.();
        return;
      }
      setError(t('modal.totpForm.invalidCode'));
    } finally {
      setLoading(false);
    }
  }, [code, message, t, fetchStatus, onTemporaryTotpExpired]);

  // Show spinner while loading data
  if (step === TotpStepEnum.Loading && loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center py-8">
        <Spin size="large" />
      </div>
    );
  }

  // Show error and retry/cancel buttons if loading fails
  if (step === TotpStepEnum.Loading && error) {
    return (
      <div className="space-y-4 py-4">
        <Typography.Text type="danger">{error}</Typography.Text>
        <Space>
          <Button onClick={initializeFlow}>{t('modal.totpForm.retry')}</Button>
          {onClose ? <Button onClick={onClose}>{t('modal.totpForm.cancel')}</Button> : null}
        </Space>
      </div>
    );
  }

  // Step: Status overview - shows whether TOTP is active/enabled, backup codes status, allow to enable/setup or disable
  if (step === TotpStepEnum.Status && status) {
    return (
      <div className="space-y-4 py-2">
        {status.enabled ? (
          <>
            {/* TOTP is enabled */}
            <div className="border-gray-medium bg-gray-light rounded-lg border p-3">
              <p className="text-green-dark font-medium">{t('modal.totpForm.statusActive')}</p>
              <p className="text-gray-dark text-sm">
                {t('modal.totpForm.activatedOn', {
                  date: dayjs(status.enabledAt).format('D MMMM YYYY'),
                })}
              </p>
              <p className="text-gray-dark text-sm">
                {t('modal.totpForm.backupCodesRemaining', {
                  count: status.backupCodesRemaining ?? 0,
                  total: 10,
                })}
              </p>
            </div>
            {/* Action buttons: disable TOTP (hidden when isSetupOnly) or regenerate backup codes */}
            <div className="flex w-full justify-between gap-2">
              {!isSetupOnly && (
                <Button danger onClick={handleDisableTotp}>
                  {t('modal.totpForm.disableTotp')}
                </Button>
              )}
              <Button
                onClick={handleRegenerateBackupCodes}
                icon={<Icon name="RotateCcwIcon" className="h-4 w-4" />}
                className={isSetupOnly ? '' : 'ml-auto'}
              >
                {t('modal.totpForm.regenerateBackupCodes')}
              </Button>
            </div>
          </>
        ) : (
          // TOTP not enabled
          <Empty
            image={<Icon name="ShieldEllipsisIcon" className="text-gray-dark mx-auto h-16 w-16" />}
            description={
              <div className="flex flex-col gap-1 text-center">
                {status.requiredByPolicy ? (
                  <>
                    <span>{t('modal.totpForm.requiredByPolicy')}</span>
                    {status.gracePeriodEnds && (
                      <span className="text-gray-dark">
                        {t('modal.totpForm.gracePeriodEnds', {
                          date: dayjs(status.gracePeriodEnds).format('D MMMM YYYY'),
                        })}
                      </span>
                    )}
                  </>
                ) : (
                  <span>{t('modal.totpForm.statusInactive')}</span>
                )}
              </div>
            }
            className="flex flex-col items-center"
          >
            {/* Button to enable TOTP */}
            <Button type="primary" onClick={handleStartSetup} loading={loading} className="mt-2">
              {t('modal.totpForm.activate')}
            </Button>
          </Empty>
        )}
      </div>
    );
  }

  // Step: Show QR code/manual secret, allow entering verification code to activate TOTP
  if (step === TotpStepEnum.Setup && setupData) {
    return (
      <div className="space-y-6 py-2 text-center">
        <Typography.Title level={5} className="text-center">
          {setupChannel === 'forcedLogin'
            ? t('auth.totpForcedSetupTitle')
            : t('modal.totpForm.setupTitle')}
        </Typography.Title>
        {setupChannel === 'forcedLogin' && (
          <p className="text-gray-dark text-center text-sm">
            {t('auth.totpForcedSetupDescription')}
          </p>
        )}
        {/* QR code for scanning */}
        <div className="flex justify-center">
          <QRCode value={setupData.uri} size={200} />
        </div>
        <p className="text-gray-dark text-center text-sm">{t('modal.totpForm.scanQR')}</p>
        {/* Allow switching to manual entry view */}
        <Button
          type="link"
          size="small"
          onClick={() => setShowManualSecret(!showManualSecret)}
          className="text-blue-dark! hover:text-azure! mx-auto block"
        >
          {showManualSecret ? t('modal.totpForm.hideManual') : t('modal.totpForm.manualEntry')}
        </Button>
        {/* Manual secret box */}
        {showManualSecret && (
          <div className="bg-gray-light rounded border p-3 text-center">
            <p className="text-gray-dark text-xs">{t('modal.totpForm.enterSecretManually')}</p>
            <code className="my-2 block font-mono text-sm break-all">
              {formatSecret(setupData.secret)}
            </code>
            <Button size="small" onClick={handleCopySecret} className="mx-auto mt-2 block">
              {t('modal.totpForm.copySecret')}
            </Button>
          </div>
        )}
        {/* TOTP verification input */}
        <div className="text-center">
          <p className="mb-2 text-sm">{t('modal.totpForm.verifyCodeLabel')}</p>
          <Input.OTP
            length={6}
            size="large"
            value={code}
            onChange={setCode}
            disabled={loading}
            status={error ? 'error' : undefined}
            inputMode="numeric"
            className="justify-center"
          />
          {error && (
            <Typography.Text type="danger" className="mt-1 block text-center text-sm">
              {error}
            </Typography.Text>
          )}
        </div>
        {/* Button to verify code and activate TOTP */}
        <Button
          type="primary"
          block
          onClick={handleVerifySetup}
          loading={loading}
          disabled={code.replace(/\s/g, '').length !== 6}
          className="mx-auto mt-2"
        >
          {t('modal.totpForm.verifyAndActivate')}
        </Button>
      </div>
    );
  }

  // Step: Enter code to regenerate backup codes (must verify with current TOTP)
  if (step === TotpStepEnum.RegenerateVerify) {
    return (
      <div className="space-y-6 py-2 text-center">
        <Typography.Title level={5}>{t('modal.totpForm.enterCodeToConfirm')}</Typography.Title>
        <Input.OTP
          length={6}
          size="large"
          value={code}
          onChange={setCode}
          disabled={loading}
          status={error ? 'error' : undefined}
          inputMode="numeric"
          className="justify-center"
        />
        {error && (
          <Typography.Text type="danger" className="block text-sm">
            {error}
          </Typography.Text>
        )}
        <div className="flex w-full justify-between gap-2">
          {/* Cancel goes back to status overview */}
          <Button onClick={() => setStep(TotpStepEnum.Status)}>{t('modal.totpForm.cancel')}</Button>
          <Button
            type="primary"
            loading={loading}
            onClick={handleSubmitRegenerateCode}
            disabled={code.replace(/\s/g, '').length !== 6}
          >
            {t('modal.totpForm.continue')}
          </Button>
        </div>
      </div>
    );
  }

  // Step: Confirm disabling TOTP (requires entering a code). Not reachable when isSetupOnly.
  if (step === TotpStepEnum.DisableVerify && !isSetupOnly) {
    return (
      <div className="space-y-6 py-2 text-center">
        <Typography.Title level={5}>{t('modal.totpForm.disableConfirm')}</Typography.Title>
        <p className="text-gray-dark text-sm">{t('modal.totpForm.disableConfirmDescription')}</p>
        <Input.OTP
          length={6}
          size="large"
          value={code}
          onChange={setCode}
          disabled={loading}
          status={error ? 'error' : undefined}
          inputMode="numeric"
          className="justify-center"
        />
        {error && (
          <Typography.Text type="danger" className="block text-sm">
            {error}
          </Typography.Text>
        )}
        <div className="flex w-full justify-between gap-2">
          {/* Cancel goes back to main status overview */}
          <Button onClick={() => setStep(TotpStepEnum.Status)}>{t('modal.totpForm.cancel')}</Button>
          <Button
            danger
            loading={loading}
            onClick={handleSubmitDisable}
            disabled={code.replace(/\s/g, '').length !== 6}
          >
            {t('modal.totpForm.disable')}
          </Button>
        </div>
      </div>
    );
  }

  // Step: Show backup codes after enabling TOTP OR after regenerating backup codes
  if (step === TotpStepEnum.BackupCodes && verifyBackupCodes.length > 0) {
    return (
      <div className="space-y-4 py-2">
        {/* Success icon and heading */}
        <div className="flex items-center gap-2 text-center">
          <Icon name="CircleCheckIcon" className="h-5 w-5" />
          <Typography.Title level={5} className="mb-0!">
            {isRegenerateFlow
              ? t('modal.totpForm.regenerateSuccess')
              : t('modal.totpForm.backupCodesTitle')}
          </Typography.Title>
        </div>
        {/* Info/warning about saving backup codes */}
        <div className="border-yellow-light bg-yellow-light text-yellow-dark rounded-lg border p-3 text-sm">
          <p>{t('modal.totpForm.backupCodesWarning')}</p>
        </div>
        {/* Display backup codes in a grid */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {verifyBackupCodes.map((c, i) => (
            <div key={i} className="rounded border bg-white p-2 font-mono text-sm">
              {c}
            </div>
          ))}
        </div>
        <div className="flex flex-row gap-2">
          <Button type="text" size="small" onClick={handleCopyAllBackupCodes}>
            <Icon name="CopyIcon" className="h-4 w-4" />
            <span>{t('modal.totpForm.copyAll')}</span>
          </Button>
          <Button type="text" size="small" onClick={handleDownloadBackupCodes}>
            <Icon name="DownloadIcon" className="h-4 w-4" />
            <span>{t('modal.totpForm.download')}</span>
          </Button>
          <Button type="text" size="small" onClick={() => window.print()}>
            <Icon name="PrinterIcon" className="h-4 w-4" />
            <span>{t('modal.totpForm.print')}</span>
          </Button>
        </div>
        {/* Checkbox for user to confirm backup codes are saved */}
        <Checkbox
          className="mb-4!"
          checked={backupCodesSaved}
          onChange={(e) => setBackupCodesSaved(e.target.checked)}
        >
          {t('modal.totpForm.savedConfirm')}
        </Checkbox>
        {/* Backup codes actions: copy, download, print, and continue */}
        <Button
          type="primary"
          block
          onClick={handleContinueAfterBackupCodes}
          disabled={!backupCodesSaved}
        >
          {t('modal.totpForm.continue')}
        </Button>
      </div>
    );
  }

  // Default (should never render)
  return null;
}
